import React, { useEffect, useState } from "react";
import { Flex, Loader, Text, Tooltip } from "@mantine/core";
import { MaterialSymbol } from "react-material-symbols";
import { useLazyCheckBackupExistsQuery, useLazyGetBackupStorageStatusQuery } from "../../../../../services/ArticleService";
import { ArticleDetails } from "../../../../../interfaces/response/ArticleDetails";
import classes from "./ArchiveStatus.module.css";

interface ArchiveStatusProps {
    article: ArticleDetails;
}

export type ArchiveState = "loading" | "unavailable" | "not_found" | "available";

interface ArchiveStatusResult {
    state: ArchiveState;
    component: React.ReactNode;
}

export function useArchiveStatus(article: ArticleDetails): ArchiveStatusResult {
    const [archiveState, setArchiveState] = useState<ArchiveState>("loading");

    const [checkStorageStatus] = useLazyGetBackupStorageStatusQuery();
    const [checkBackupExists] = useLazyCheckBackupExistsQuery();

    useEffect(() => {
        const checkArchive = async () => {
            try {
                setArchiveState("loading");
                
                const statusResult = await checkStorageStatus().unwrap();
                
                if (!statusResult.available) {
                    setArchiveState("unavailable");
                    return;
                }

                const sku = article.headerArticleDetails.code;
                const existsResult = await checkBackupExists(sku).unwrap();
                
                if (existsResult) {
                    setArchiveState("available");
                } else {
                    setArchiveState("not_found");
                }
            } catch (error) {
                console.error("Error checking archive status:", error);
                setArchiveState("unavailable");
            }
        };

        checkArchive();
    }, [article.headerArticleDetails.code, checkStorageStatus, checkBackupExists]);

    const component = <ArchiveStatusDisplay state={archiveState} />;

    return { state: archiveState, component };
}

function ArchiveStatusDisplay({ state }: { state: ArchiveState }) {
    if (state === "loading") {
        return (
            <Flex className={classes.container} align="center" gap="xs">
                <Loader size="xs" color="var(--mantine-color-blue-6)" />
                <Text size="sm" c="dimmed">Verifica archivio...</Text>
            </Flex>
        );
    }

    if (state === "unavailable") {
        return (
            <Tooltip label="Impossibile connettersi al database di backup" position="bottom">
                <Flex className={classes.containerError} align="center" gap="xs">
                    <MaterialSymbol icon="cloud_off" size={16} color="var(--mantine-color-red-6)" />
                    <Text size="xs" c="var(--mantine-color-red-7)" fw={500}>
                        Archivio non disponibile
                    </Text>
                </Flex>
            </Tooltip>
        );
    }

    if (state === "available") {
        return (
            <Flex className={classes.containerSuccess} align="center" gap="xs">
                <MaterialSymbol icon="inventory_2" size={16} color="var(--mantine-color-teal-7)" />
                <Text size="xs" c="var(--mantine-color-teal-8)" fw={500}>
                    Backup disponibile
                </Text>
            </Flex>
        );
    }

    return null;
}

export default function ArchiveStatus({ article }: ArchiveStatusProps) {
    const { component } = useArchiveStatus(article);
    return <>{component}</>;
}
